import React, { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Batch } from "generated/graphql";

import { useInjection } from "@di/DependencyContext";
import { DependencyType } from "@di/DependencyType";
import { BatchService } from "@modules/batch/batch.service";
import { LoadingState } from "@util/loadingstate.util";
import { ParsedError, parseServerError } from "@util/parseError";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { Button } from "~/components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Spinner } from "~/components/ui/spinner";

export interface BatchFormProps {
  onClose: () => void;
  onSubmit: () => void;
  batch?: Batch;
}

const schema = yup.object({
  // Regex for YYMMDD formatted date string
  batchNumber: yup
    .string()
    .required("Batch number is required")
    .length(6)
    .matches(
      /^(0[1-9]|1[0-9]|2[0-9]|3[0-1])(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1])$/,
      "Enter a YYMMDD formatted date"
    ),
  name: yup.string().required("Batch name is required").min(3),
  description: yup.string().optional(),
});
type BatchFormValues = yup.InferType<typeof schema>;

export const BatchForm: React.FC<BatchFormProps> = (props) => {
  const isEditing = Boolean(props.batch);
  const form = useForm<BatchFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      batchNumber: props.batch?.batchNumber ?? "",
      name: props.batch?.name ?? "",
      description: props.batch?.description ?? "",
    },
  });
  const batchService = useInjection<BatchService>(DependencyType.BatchService);
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.Idle
  );
  const [error, setError] = useState<ParsedError>();

  const onSubmit = async (data: BatchFormValues) => {
    try {
      setError(undefined);
      setLoadingState(LoadingState.Loading);
      if (props.batch) {
        await batchService.updateBatch({
          id: props.batch.id,
          ...data,
        });
      } else {
        await batchService.createBatch(data);
      }
      setLoadingState(LoadingState.Loaded);
      props.onSubmit();
      form.reset();
      props.onClose();
    } catch (e) {
      const parsed = parseServerError(e);
      console.error(parsed);
      setError(parsed);
      setLoadingState(LoadingState.Error);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
      >
        <DialogHeader className="gap-3 text-left">
          <DialogTitle>
            {isEditing ? "Edit batch" : "Create new batch"}
          </DialogTitle>
          <DialogDescription>
            Batches are used to track the manufacturing dates of PES products.
            Batches may include multiple product types.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="batchNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Batch number</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    inputMode="numeric"
                    placeholder="240116"
                    maxLength={6}
                    onChange={(event) => {
                      const digits = event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);
                      field.onChange(digits);
                    }}
                    required
                  />
                </FormControl>
                <FormDescription>6 digits formatted YYMMDD</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Batch name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder="First build"
                    required
                  />
                </FormControl>
                <FormDescription>Min 3 characters, unique</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Batch description</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ""}
                    placeholder="First 3 units from 30off"
                    rows={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {loadingState === LoadingState.Error && error && (
          <p className="text-sm text-destructive">{error.message}</p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              form.reset();
              props.onClose();
            }}
            disabled={loadingState === LoadingState.Loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loadingState === LoadingState.Loading}
          >
            {loadingState === LoadingState.Loading && (
              <Spinner className="mr-2 size-4" />
            )}
            Save
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
